import { FC, type ReactNode, useState } from "react";

import { useAv } from "../../../../utils/av";
import { MIN_GROUP_COUNT, useBreakouts } from "../../../../utils/breakouts";
import { Link } from "../../../../utils/routing/components/Link";
import { pageUrl } from "../../../../utils/urls";

import { Trans, t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { ToggleSwitch } from "mds/components/ToggleSwitch";
import { Tooltip } from "mds/components/Tooltip";
import { TooltipTrigger } from "mds/components/TooltipTrigger";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { RefreshIcon } from "mds/icons";
import { store, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectActiveClassSession,
  selectActiveClassSessionId,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { useObjectSelector } from "store/store-hooks";

interface GroupConfigBreakoutsBannerProps {
  pageId: string;
  hasAssessments: boolean;
  saveDuration: () => Promise<number | null>;
  children?: ReactNode;
  durationAction?: ReactNode;
}

export const GroupConfigBreakoutsBanner: FC<GroupConfigBreakoutsBannerProps> = ({
  pageId,
  hasAssessments,
  saveDuration,
  children,
  durationAction,
}) => {
  const { isInVirtualClass, isVirtualClassActive } = useAv();
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const { onStartBreakouts } = useBreakouts();
  // When the page has assessments, sync must stay off; otherwise keep the user's toggle.
  const [userWantsSync, setUserWantsSync] = useState(true);
  const shouldSyncBreakoutsWithPageGroups = !hasAssessments && userWantsSync;
  const classSession = useObjectSelector(selectActiveClassSession, [
    "breakout_session_synced_page_id",
  ]);

  const syncedPage = useObjectSelector(
    (s) => pageSelectors.selectById(s, classSession?.breakout_session_synced_page_id),
    ["id", "title", "section_id"],
  );

  const onStart = async () => {
    const sessionId = selectActiveClassSessionId(store.getState());
    const duration = await saveDuration();
    // Another instructor may start breakouts while the duration saves. Recheck the
    // latest state so we don't replace their rooms or reset their timer.
    const state = store.getState();
    if (
      duration === null ||
      selectActiveClassSessionId(state) !== sessionId ||
      selectIsInBreakout(state)
    )
      return;
    await onStartBreakouts(
      "page",
      duration,
      pageId,
      MIN_GROUP_COUNT,
      shouldSyncBreakoutsWithPageGroups,
    );
  };

  const durationCard = children && (
    <Card className="w-full @container/breakout-config">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="w-full @[560px]/breakout-config:w-1/2 @[560px]/breakout-config:min-w-[20rem]">
          {children}
        </div>
        {durationAction}
      </div>
    </Card>
  );

  if (
    !classSession ||
    !isInVirtualClass ||
    !isVirtualClassActive ||
    (isInBreakout && !classSession.breakout_session_synced_page_id)
  )
    return durationCard;

  if (isInBreakout) {
    if (classSession?.breakout_session_synced_page_id === pageId) {
      return (
        <>
          {durationCard}
          <Card className="flex w-full flex-wrap items-center justify-between">
            <div className="flex items-center gap-1 text-sm font-medium text-black-tint-20">
              <RefreshIcon />
              {t("group_configuration_card.breakouts_synced_with_this_page")}
            </div>
            <TooltipTrigger
              content={t("group_configuration_card.breakouts_synced_with_this_page_tooltip")}
            />
          </Card>
        </>
      );
    }

    if (classSession.breakout_session_synced_page_id && !!syncedPage) {
      return (
        <>
          {durationCard}
          <Card className="flex w-full items-center justify-between">
            <div className="flex items-center gap-1 truncate text-sm font-medium text-black-tint-20">
              <RefreshIcon />
              <Trans
                i18nKey="group_configuration_card.breakouts_synced_with_another_page"
                t={t}
                values={{ title: syncedPage.title }}
              >
                <Link
                  className="truncate text-blue"
                  to={pageUrl(syncedPage.section_id, syncedPage.id)}
                >
                  {syncedPage.title}
                </Link>
              </Trans>
            </div>
            <TooltipTrigger
              content={t("group_configuration_card.breakouts_synced_with_another_page_tooltip")}
            />
          </Card>
        </>
      );
    }
    return durationCard;
  }

  return (
    <Card className="w-full @container/breakout-config">
      <div
        className={
          children
            ? "flex flex-col gap-4 @[560px]/breakout-config:flex-row @[560px]/breakout-config:items-start @[560px]/breakout-config:justify-between"
            : "flex w-full flex-wrap items-center justify-between"
        }
      >
        <div
          className={
            children
              ? "flex w-full flex-col gap-4 @[560px]/breakout-config:w-1/2 @[560px]/breakout-config:min-w-[20rem]"
              : undefined
          }
        >
          <Tooltip
            content={
              hasAssessments
                ? t("group_configuration_card.breakouts_synced_disabled_tooltip")
                : undefined
            }
          >
            <ToggleSwitch
              className={children ? "min-h-8 w-full justify-between text-left" : undefined}
              disabled={hasAssessments}
              eventName="Group Settings - Keep breakouts in sync"
              isSelected={shouldSyncBreakoutsWithPageGroups}
              labelStart={
                <div className="flex items-center gap-1 text-sm font-medium text-black-tint-20">
                  <RefreshIcon />
                  {t("group_configuration_card.keep_groups_and_breakouts_in_sync")}
                </div>
              }
              onClick={() => setUserWantsSync(!userWantsSync)}
            />
          </Tooltip>
          {children}
        </div>
        <SquareButton
          className={children ? "shrink-0 self-end @[560px]/breakout-config:self-start" : undefined}
          eventName="Group Settings - Start breakouts"
          kind="accent"
          size="s"
          data-saves-duration
          disableWhilePending
          onClick={onStart}
        >
          {t("breakout_group_modal.start_breakouts")}
        </SquareButton>
      </div>
    </Card>
  );
};
